import { H2, H3 } from '@berlitz/globals'
import Modal from '@berlitzplatforms/micro.ui.modal'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useBlockedInstructors } from '../context'
import { Label } from '@layouts/Settings/Account/style'
import Select from '@components/Select'
import Spacer from '@berlitz/spacer'
import { BLOCK_FOR, REASONS, THIS_COURSE } from './constants'
import Button from '@berlitz/button'
import { FieldWrapper, Footer, Loader } from './style'
import Checkbox from '@berlitz/checkbox'
import { GET_FUTURE_SESSIONS } from '@queries/futureSessions'
import Spinner from '@berlitz/spinner'
import { GetFutureSessionsResult } from '@interfaces/BlockInstructor'
import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import EmptyState from '@components/EmptyState'
import { ErorrWrapper } from '../BulkForm/SelectInstructors/style'
import { BlockModalProps, IPBlockFormValues } from './intreface'
import { IPPutRelatedInstructorsResponse } from '../interface'
import { PUT_RELATED_INSTRUCTORS } from '../mutations'
import ModalTransactional from '@berlitzplatforms/micro.modal.transactional'
import { useRouter } from 'next/router'
import { SCHEDULING_LEVEL } from '@utils/constants'
import useRefetchProfile from '@hooks/useRefetchProfile'

const BlockModal: React.FC<BlockModalProps> = ({ open, onClose, onCompleted }) => {
  const router = useRouter()
  const intl = useIntl()
  const blocked = useBlockedInstructors()
  const refetchProfile = useRefetchProfile()
  const defaultValues = {
    reason: '',
    blockedFor: '',
    futureLessonsBookedWillBeCancelled: false,
    supportCaseRaisedForSessionsWithin24Hours: false,
  }
  const [formValues, setFormValues] = useState<IPBlockFormValues>(defaultValues)
  const [displayConfirmation, setDisplayConfirmation] = useState(false)
  const [fetchingUserProfile, setFetchingUserProfile] = useState(false)

  const [getFutureSessions, { data, loading, error }] = useLazyQuery<GetFutureSessionsResult>(GET_FUTURE_SESSIONS, {
    fetchPolicy: 'network-only',
  })
  const displayField = blocked.getBlockFormFields(data)
  const hasFutureSessions = data?.getFutureSessions?.[0]?.Sessions || data?.getFutureSessions?.[0]?.Next24Hours
  const [putRelatedInstructors, { loading: putLoading, error: putError }] =
    useMutation<IPPutRelatedInstructorsResponse>(PUT_RELATED_INSTRUCTORS, {
      onCompleted: async (putData) => {
        if (putData?.putRelatedInstructors?.Success) {
          setFetchingUserProfile(true)
          const [updateUI] = await refetchProfile()
          updateUI && (await updateUI())
          setFetchingUserProfile(false)
          setDisplayConfirmation(true)
          onCompleted(blocked?.selectedInstructor?.InstructorId!)
          blocked.setBlockedInstructors([
            ...blocked.blockedInstructors,
            {
              ...blocked.selectedInstructor,
              IsBlocked: true,
              BlockedReason: formValues.reason,
              SchedulingLevel:
                formValues?.blockedFor === THIS_COURSE
                  ? SCHEDULING_LEVEL.PROGRAM_LEVEL
                  : SCHEDULING_LEVEL.PROFILE_LEVEL,
            },
          ])
        }
      },
    })

  useEffect(() => {
    if (!open) {
      setFormValues(defaultValues)
      setDisplayConfirmation(false)
    }
  }, [open])

  useEffect(() => {
    if (open && blocked.selectedInstructor) {
      getFutureSessions({
        variables: blocked.getPayload('get', [blocked.selectedInstructor]),
      })
    }
  }, [open, blocked.selectedInstructor])

  const handleClose = () => (putLoading ? null : onClose())
  const handleRedirect = () =>
    router.pathname === '/scheduling/instructors' ? handleClose() : router.push('/scheduling/instructors')

  return (
    <Modal open={open} onClose={handleClose}>
      {displayConfirmation ? (
        <>
          <ModalTransactional
            status="success"
            title={
              <>
                {blocked.selectedInstructor?.Name} <FormattedMessage id="Blocked" defaultMessage="Blocked" />
              </>
            }
            description={
              hasFutureSessions ? (
                <FormattedMessage
                  id={'You now have lessons you need to re-book, would you like to book these lessons now?'}
                  defaultMessage={'You now have lessons you need to re-book, would you like to book these lessons now?'}
                />
              ) : undefined
            }
          />

          <Footer>
            {hasFutureSessions ? (
              <>
                <Button onClick={handleClose}>
                  <FormattedMessage id="No" defaultMessage="No" />
                </Button>
                <Button disabled={false} onClick={() => handleRedirect()}>
                  <FormattedMessage id="Yes" defaultMessage="Yes" />
                </Button>
              </>
            ) : (
              <Button onClick={handleClose}>
                <FormattedMessage id="Ok" defaultMessage="Ok" />
              </Button>
            )}
          </Footer>
        </>
      ) : (
        <>
          {error || putError ? (
            <ErorrWrapper>
              <Spacer size="md" />
              <H3 disableMargin>
                <FormattedMessage id="Oops, something went wrong." defaultMessage="Oops, something went wrong." />
              </H3>
              <EmptyState heading="Please try again later or contact the Support team." img="/images/bug.svg" />
            </ErorrWrapper>
          ) : loading || fetchingUserProfile ? (
            <Loader>
              <Spacer size="xxxl" />
              <Spinner size="md" />
              <Spacer size="xxxl" />
            </Loader>
          ) : (
            <>
              <H2>
                <FormattedMessage
                  id="Block {name}"
                  defaultMessage="Block {name}"
                  values={{ name: blocked.selectedInstructor?.Name }}
                />
              </H2>

              <Spacer size="xxs" />

              <FieldWrapper>
                {displayField?.reason ? (
                  <>
                    <div id="select-reason">
                      <Label>
                        <FormattedMessage id="Select reason" defaultMessage="Select reason" />
                      </Label>
                      <Select
                        options={REASONS.map((reason) => ({
                          id: reason,
                          label: reason,
                          value: reason,
                        }))}
                        name="select-reason"
                        onChange={(val) => setFormValues({ ...formValues, reason: val })}
                        fullWidth
                        getPopupContainer={() => document.getElementById('select-reason')}
                        placeholderText={intl.formatMessage({
                          id: 'Select',
                          defaultMessage: 'Select',
                        })}
                        value={
                          formValues.reason
                            ? formValues.reason
                            : intl.formatMessage({
                                id: 'Select',
                                defaultMessage: 'Select',
                              })
                        }
                        disabled={putLoading}
                      />
                    </div>

                    <Spacer size="md" />
                  </>
                ) : null}

                {displayField?.blockedFor ? (
                  <>
                    <div id="block-instructor-for">
                      <Label>
                        <FormattedMessage id="Block instructor for:" defaultMessage="Block instructor for:" />
                      </Label>
                      <Select
                        options={BLOCK_FOR.map((blockFor) => ({
                          id: blockFor,
                          label: blockFor,
                          value: blockFor,
                        }))}
                        name="block-instructor-for"
                        onChange={(val) => setFormValues({ ...formValues, blockedFor: val })}
                        fullWidth
                        getPopupContainer={() => document.getElementById('block-instructor-for')}
                        placeholderText={intl.formatMessage({
                          id: 'Select',
                          defaultMessage: 'Select',
                        })}
                        value={
                          formValues.blockedFor
                            ? formValues.blockedFor
                            : intl.formatMessage({
                                id: 'Select',
                                defaultMessage: 'Select',
                              })
                        }
                        disabled={putLoading}
                      />
                    </div>

                    <Spacer size="md" />
                  </>
                ) : null}

                {displayField?.futureLessonsBookedWillBeCancelled ? (
                  <>
                    <Checkbox
                      name="futureLessonsBookedWillBeCancelled"
                      label={intl.formatMessage({
                        id: 'I understand that any future sessions booked with this instructor will be cancelled.',
                        defaultMessage:
                          'I understand that any future sessions booked with this instructor will be cancelled.',
                      })}
                      value="yes"
                      onChange={() =>
                        putLoading
                          ? null
                          : setFormValues({
                              ...formValues,
                              futureLessonsBookedWillBeCancelled: !formValues.futureLessonsBookedWillBeCancelled,
                            })
                      }
                      checked={formValues.futureLessonsBookedWillBeCancelled}
                      disabled={putLoading}
                    />

                    <Spacer size="sm" />
                  </>
                ) : null}

                {displayField?.supportCaseRaisedForSessionsWithin24Hours ? (
                  <>
                    <Checkbox
                      name="supportCaseRaisedForSessionsWithin24Hours"
                      label={intl.formatMessage({
                        id: 'I understand that a support case will be raised for any sessions occurring within 24 hours.',
                        defaultMessage:
                          'I understand that a support case will be raised for any sessions occurring within 24 hours.',
                      })}
                      value="yes"
                      onChange={() =>
                        putLoading
                          ? null
                          : setFormValues({
                              ...formValues,
                              supportCaseRaisedForSessionsWithin24Hours:
                                !formValues.supportCaseRaisedForSessionsWithin24Hours,
                            })
                      }
                      checked={formValues.supportCaseRaisedForSessionsWithin24Hours}
                      disabled={putLoading}
                    />
                  </>
                ) : null}
              </FieldWrapper>

              <Spacer size="xxl" />

              <Footer>
                <Button onClick={handleClose} disabled={putLoading}>
                  <FormattedMessage id="Cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  disabled={blocked.disableBlockButton(formValues, data)}
                  onClick={() =>
                    blocked.selectedInstructor &&
                    putRelatedInstructors({
                      variables: blocked.getPayload('put', [blocked.selectedInstructor], formValues, data),
                      refetchQueries: ['GetInstructorsByProgramId'],
                    })
                  }
                  loading={putLoading}
                  loadingText={`${intl.formatMessage({
                    id: 'Blocking',
                    defaultMessage: 'Blocking',
                  })}...`}
                  canLoad
                >
                  <FormattedMessage id="Block" defaultMessage="Block" />
                </Button>
              </Footer>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default React.memo(BlockModal)
